import { AddCircle, RemoveCircle } from '@mui/icons-material';
import {
  Box,
  Button,
  FormHelperText,
  IconButton,
  TextField,
  TextFieldProps,
  Tooltip,
} from '@mui/material';
import { FormikHandlers, FormikHelpers, FormikState } from 'formik';
import React, { useMemo } from 'react';

// Remove the properties that we end up manually setting as part of setting up formik
type ModifiedListProps = Omit<
  TextFieldProps,
  | 'name'
  | 'value'
  | 'onChange'
  | 'helperText'
  | 'error'
  | 'disabled'
  | 'fullWidth'
>;

/**
 * The properties for the formik list field
 */
type FormikListFieldProps<T extends object, K extends keyof T & string> = {
  formik: FormikState<T> & FormikHandlers & FormikHelpers<T>;
  name: K;
  itemName: string;
  helpText?: string;
  disabled?: boolean;
  additionalOnChange?: (e: React.ChangeEvent<any>) => void;
} & ModifiedListProps;

/**
 * The FormikListField component manages an array of string inputs
 */
const FormikListField = <
  T extends Record<K, string[]>,
  K extends keyof T & string,
>(
  props: FormikListFieldProps<T, K>
) => {
  const { formik, name, additionalOnChange, ...rest } = props;
  const errors = useMemo(() => {
    const formikErrors = formik.errors[name];
    if (!Array.isArray(formikErrors)) return formik.errors[name] as string;
    if (formikErrors == undefined) return undefined;
    return formikErrors as [string];
  }, [formik, name]);
  const touched = useMemo(() => {
    if (formik.touched[name] == undefined) return undefined;
    return formik.touched[name] as unknown as [string];
  }, [formik, name]);

  const handleAdd = () => {
    const updatedValues = [...formik.values[name], ''];
    formik.setFieldValue(name, updatedValues);
  };

  const handleRemove = (index: number) => {
    const updatedValues = formik.values[name].filter((_, i) => i !== index);
    formik.setFieldValue(name, updatedValues);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    if (additionalOnChange) additionalOnChange(e);
    const updatedValues = [...formik.values[name]];
    updatedValues[index] = e.target.value;
    formik.setFieldValue(name, updatedValues);
  };

  return (
    <Box display="flex" gap="1em" flexDirection="column">
      {formik.values[name]?.map((value: string, index: number) => (
        <Box key={index} display="flex" gap=".5em">
          <TextField
            value={value}
            onChange={(e) => handleChange(e, index)}
            helperText={
              ((Array.isArray(errors) &&
                touched?.[index] &&
                errors?.[index]) as string | undefined) ?? props.helpText
            }
            disabled={formik.isSubmitting || props.disabled}
            error={
              Array.isArray(errors) &&
              Boolean(touched?.[index] && errors?.[index])
            }
            fullWidth
            {...(rest as ModifiedListProps)}
          />
          <Box display="flex">
            <Tooltip title={`Remove ${props.itemName}`} arrow>
              <IconButton
                onClick={() => handleRemove(index)}
                color="error"
                disabled={formik.isSubmitting || props.disabled}
              >
                <RemoveCircle />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ))}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <Button
          type="button"
          variant="contained"
          startIcon={<AddCircle />}
          onClick={handleAdd}
          color="success"
          disabled={formik.isSubmitting || props.disabled}
        >
          Add {props.itemName}
        </Button>
        <FormHelperText
          sx={{
            marginTop: '1em',
          }}
          error={!Array.isArray(errors) && Boolean(touched?.[0] && errors)}
        >
          {
            (!Array.isArray(errors) &&
              props.formik.touched[props.name] &&
              props.formik.errors[props.name]) as string
          }
        </FormHelperText>
      </Box>
    </Box>
  );
};

export default FormikListField;
